// The two answer shapes a job's screening question can have — the UI-facing
// half of the server's `AnswerType` enum (server/app/config/enum_options.py,
// which derives its labels from app/db/models.py). One list: the job form's
// answer-type select, the zod enum in pages/admin/job/validation.schema.js and
// the candidate form's branch all read it from here.
export const ANSWER_TYPE = {
  YES_NO: "YES_NO",
  SHORT_TEXT: "SHORT_TEXT",
};

export const ANSWER_TYPE_OPTIONS = [
  { label: "Yes / No", value: ANSWER_TYPE.YES_NO },
  { label: "Short Text", value: ANSWER_TYPE.SHORT_TEXT },
];

export const ANSWER_TYPE_VALUES = ANSWER_TYPE_OPTIONS.map((o) => o.value);

// The closed vocabulary a style guide's tone may be written in — the
// UI-facing half of server/app/config/tone_keywords.py, the same arrangement
// ANSWER_TYPE has with enum_options.py. The keywords reach an LLM prompt
// verbatim, so the server refuses anything off this list: a word added here
// and not there is a 422 the recruiter cannot act on.
export const TONE_KEYWORD_OPTIONS = [
  { label: "Professional", value: "professional" },
  { label: "Friendly", value: "friendly" },
  { label: "Inclusive", value: "inclusive" },
  { label: "Warm", value: "warm" },
  { label: "Concise", value: "concise" },
  { label: "Direct", value: "direct" },
  { label: "Conversational", value: "conversational" },
  { label: "Formal", value: "formal" },
  { label: "Casual", value: "casual" },
  { label: "Confident", value: "confident" },
  { label: "Empathetic", value: "empathetic" },
  { label: "Respectful", value: "respectful" },
  { label: "Encouraging", value: "encouraging" },
  { label: "Enthusiastic", value: "enthusiastic" },
  { label: "Personable", value: "personable" },
  { label: "No jargon", value: "no-jargon" },
];

export const TONE_KEYWORD_VALUES = TONE_KEYWORD_OPTIONS.map((o) => o.value);

export const NOTICE_PERIOD_OPTIONS = [
  { label: "Immediate", value: "Immediate" },
  { label: "15 days", value: "15 days" },
  { label: "30 days", value: "30 days" },
  { label: "60 days", value: "60 days" },
  { label: "90 days", value: "90 days" },
  { label: "More than 90 days", value: "More than 90 days" },
];

export const JOB_STATUS = {
  JOB_OPEN: "Open",
  JOB_CLOSE: "Close",
};

export const JOB_STATUS_LIST = [
  { label: "Open", value: "OPEN" },
  { label: "Closed", value: "CLOSED" },
];

export const EMPLOYEE_TYPE = [
  { label: "Full-time", value: "FULL_TIME" },
  { label: "Part-time", value: "PART_TIME" },
  { label: "Contract", value: "CONTRACT" },
  { label: "Internship", value: "INTERNSHIP" },
  { label: "Hybrid", value: "HYBRID" },
  { label: "Remote", value: "REMOTE" },
];

export const JOB_PRIORITY = [
  { label: "High", value: "HIGH" },
  { label: "Normal", value: "NORMAL" },
  { label: "Low", value: "LOW" },
];

export const STATUS = [
  { label: "Active", value: "true" },
  { label: "Inactive", value: "false" },
];

export const USER_STATUS = [
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
  { label: "Pending", value: "pending" },
];
// The default proficiency levels every language picker starts from. The one
// copy: the server stores a level as free text and never checks it against a
// list, so nothing server-side mirrors this. It ships with the client because
// the public career page has no token to fetch it with, the same reason the ISO
// language catalogue ships this way.
//
// Closed set: both pickers offer exactly these levels, nothing free-typed.
// Values ARE the display strings, because a default and a legacy custom level
// land in the same free-text column server-side. "None" is a real level, not
// an empty field — it says the person was asked about this language and does
// not know it at all, which `language_score.py` (`reconcile_language_matches`)
// reads as a sentinel and scores exactly like the language being absent.
export const PROFICIENCY_LEVELS = ["None", "Beginner", "Intermediate", "Proficient", "Advanced", "Expert", "Native"];

// A level with no requirement to compare it against: the recruiter is
// entering the candidate's data (bulk upload, reupload, edit profile, admin
// apply-to-job) and simply was not told what the candidate's level is. Never
// offered where the person filling the row IS the candidate (apply,
// confirm-details) — nobody is unsure of their own level — nor on the job's
// own required-level picker, which asks what the role needs, not what anyone
// knows about a candidate. `language_score.py` treats it exactly like a blank
// proficiency: present, level not stated.
export const RECRUITER_UNKNOWN_PROFICIENCY = "Unknown";
export const RECRUITER_PROFICIENCY_LEVELS = [...PROFICIENCY_LEVELS, RECRUITER_UNKNOWN_PROFICIENCY];

// Product feedback — the UI-facing half of the server's `FeedbackType` and
// `FeedbackStatus` (server/app/db/models.py; the server's own words for the
// two mails are in server/app/config/enum_options.py). Labels only: which
// statuses a type allows comes from each item's `allowed_statuses`, served
// from server/app/api/v2/feedback/workflow.py, so no copy of that rule lives here.
export const FEEDBACK_TYPE_OPTIONS = [
  { label: "Issue", value: "ISSUE" },
  { label: "Suggestion", value: "SUGGESTION" },
  { label: "Appreciation", value: "APPRECIATION" },
  { label: "Other", value: "OTHER" },
];

export const FEEDBACK_TYPE_VALUES = FEEDBACK_TYPE_OPTIONS.map((o) => o.value);

// `colorIndex` is a StatusBadge palette slot: 0 blue for new, 2 amber while
// someone works on it, 3 emerald for a good outcome, 4 violet for one kept for
// later, 8 slate for a closed one.
export const FEEDBACK_STATUS_OPTIONS = [
  { label: "Open", value: "OPEN", colorIndex: 0 },
  { label: "Addressed", value: "ADDRESSED", colorIndex: 3 },
  { label: "Not addressing", value: "NOT_ADDRESSING", colorIndex: 8 },
  { label: "Implemented", value: "IMPLEMENTED", colorIndex: 3 },
  { label: "Not proceeding", value: "NOT_PROCEEDING", colorIndex: 8 },
  { label: "Future consideration", value: "FUTURE_CONSIDERATION", colorIndex: 4 },
  { label: "In progress", value: "IN_PROGRESS", colorIndex: 2 },
  { label: "Resolved", value: "RESOLVED", colorIndex: 3 },
  { label: "Closed", value: "CLOSED", colorIndex: 8 },
];

// Lookups by value, for the screens that show a stored type or status.
export const FEEDBACK_TYPE_LABEL = Object.fromEntries(FEEDBACK_TYPE_OPTIONS.map((o) => [o.value, o.label]));
export const FEEDBACK_STATUS_LABEL = Object.fromEntries(FEEDBACK_STATUS_OPTIONS.map((o) => [o.value, o.label]));
export const FEEDBACK_STATUS_COLOR = Object.fromEntries(FEEDBACK_STATUS_OPTIONS.map((o) => [o.value, o.colorIndex]));

// The same numbers and words the server refuses with
// (server/app/api/v2/feedback/schema/api.py and schema/exceptions.py).
// Python and JS share no constants channel, so the pair is kept in step by
// hand — see .claude/rules/registries.md.
export const FEEDBACK_LIMITS = {
  commentMaxLength: 2000,
  maxImages: 3,
  imageMaxBytes: 5 * 1024 * 1024,
  imageMimeTypes: ["image/png", "image/jpeg"],
  // The reason a SuperAdmin gives for every status change.
  statusReasonMaxLength: 1000,
  // A comment and a closing reason must say something in words: "12345" or
  // "!!!" is refused, "Error 404 on page 2" is not. The server's
  // `has_letter` (app/core/validators.py) is the same rule.
  hasLetter: /\p{L}/u,
  messages: {
    tooManyImages: "You can attach up to 3 images.",
    imageInvalid: "Images must be PNG or JPEG.",
    imageTooLarge: "Each image must be 5 MB or smaller.",
    commentNeedsLetter: "Comment must contain letters and cannot be only numbers, spaces or special characters.",
    reasonNeedsLetter: "Reason must contain letters and cannot be only numbers, spaces or special characters.",
  },
};

// The file kinds the SuperAdmin's import reads, and the limits the server's
// FEEDBACK_IMPORT_MAX_BYTES and FEEDBACK_IMPORT_MAX_ROWS hold
// (app/api/v2/feedback/schema/api.py) — by hand, see .claude/rules/registries.md.
// Its columns live on the server alone, which builds the template the page offers.
export const FEEDBACK_IMPORT = {
  accept: ".csv,.xlsx",
  maxBytes: 5 * 1024 * 1024,
  messages: {
    limits: "Max 5 MB, up to 1,000 rows.",
    fileTooLarge: "The file must be 5 MB or smaller.",
  },
};
